
import React, { useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Calendar, Phone, Send, X, Check } from "lucide-react";
import { sendSlackMessage } from "@/lib/sendSlackMessage";
import { useSearchParams } from "react-router-dom";

import advisor1 from "@/assets/advisors/advisor1.jpg";
import advisor2 from "@/assets/advisors/advisor2.jpg";
import advisor3 from "@/assets/advisors/advisor3.jpg";
import advisor4 from "@/assets/advisors/advisor4.jpg";

const SLACK_CHANNEL = "C0ATGPU3Y3E";
const advisors = [advisor1, advisor2, advisor3, advisor4];

const getBriefDataFromStorage = (sessionId: string | null): Record<string, string> => {
  const data: Record<string, string> = {};
  try {
    if (sessionId) {
      const raw = sessionStorage.getItem(`brief_data_${sessionId}`);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed.email) data.email = parsed.email;
        if (parsed.eventType) data.eventType = parsed.eventType;
        if (parsed.budget) data.budget = parsed.budget;
        if (parsed.participants) data.participants = parsed.participants;
        if (parsed.location) data.location = parsed.location;
        if (parsed.startDate) data.startDate = parsed.startDate;
        if (parsed.endDate) data.endDate = parsed.endDate;
      }
      const venuesRaw = sessionStorage.getItem(`suggested_venues_${sessionId}`);
      if (venuesRaw) {
        const venues = JSON.parse(venuesRaw);
        if (Array.isArray(venues) && venues.length > 0) {
          data.venues = venues.join(", ");
        }
      }
    }
  } catch {
    // ignore
  }
  return data;
};

const buildSlackMessage = (
  contactType: "rdv" | "appel",
  contactDetail: string,
  briefData: Record<string, string>
): string => {
  const lines: string[] = [
    `🔔 *Nouvelle demande de contact — Suggestions AI*`,
    ``,
    `*Type de contact* : ${contactType === "rdv" ? "📅 Rendez-vous pris" : "📞 Demande de rappel"}`,
  ];

  if (contactType === "rdv") {
    lines.push(`*Lien RDV* : https://meetings.hubspot.com/jb-dufoort`);
  } else {
    lines.push(`*Téléphone* : ${contactDetail}`);
  }

  if (Object.keys(briefData).length > 0) {
    lines.push(``);
    lines.push(`--- Infos du brief ---`);
    if (briefData.email) lines.push(`*Email* : ${briefData.email}`);
    if (briefData.eventType) lines.push(`*Type d'événement* : ${briefData.eventType}`);
    if (briefData.participants) lines.push(`*Participants* : ${briefData.participants}`);
    if (briefData.budget) lines.push(`*Budget* : ${briefData.budget}`);
    if (briefData.location) lines.push(`*Lieu* : ${briefData.location}`);
    if (briefData.startDate) lines.push(`*Date début* : ${briefData.startDate}`);
    if (briefData.endDate) lines.push(`*Date fin* : ${briefData.endDate}`);
    if (briefData.venues) lines.push(`*Lieux suggérés* : ${briefData.venues}`);
  }

  return lines.join("\n");
};

interface SuggestionsHeaderActionsProps {
  isScrolled: boolean;
}

const SuggestionsHeaderActions: React.FC<SuggestionsHeaderActionsProps> = ({ isScrolled }) => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");

  const [expanded, setExpanded] = useState(false);
  const [activeMode, setActiveMode] = useState<"rdv" | "appel">("rdv");
  const [meetingOpen, setMeetingOpen] = useState(false);
  const [phone, setPhone] = useState("");
  const [phoneSent, setPhoneSent] = useState(false);
  const [sending, setSending] = useState(false);

  const handleToggle = (mode: "rdv" | "appel") => {
    setActiveMode(mode);
    if (mode === "rdv") {
      setMeetingOpen(true);
    }
  };

  const handlePhoneSubmit = async () => {
    if (!phone || phone.length < 6) return;
    setSending(true);
    try {
      const briefData = getBriefDataFromStorage(sessionId);
      const message = buildSlackMessage("appel", phone, briefData);
      await sendSlackMessage(message, SLACK_CHANNEL);
      setPhoneSent(true);
      setTimeout(() => {
        setPhoneSent(false);
        setPhone("");
      }, 2500);
    } catch (err) {
      console.error("Slack notification failed:", err);
    } finally {
      setSending(false);
    }
  };

  const handleMeetingBooked = async () => {
    try {
      const briefData = getBriefDataFromStorage(sessionId);
      const message = buildSlackMessage("rdv", "", briefData);
      await sendSlackMessage(message, SLACK_CHANNEL);
    } catch (err) {
      console.error("Slack notification failed:", err);
    }
  };

  React.useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.data?.meetingBookSucceeded || event.data?.type === "hsFormCallback") {
        handleMeetingBooked();
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [sessionId]);

  return (
    <>
      <div className="hidden md:flex flex-col items-end relative">
        <div className="flex items-center gap-3">
          {/* Avatars - always visible */}
          <div className="relative flex items-center">
            <div className="flex -space-x-2.5">
              {advisors.map((src, i) => (
                <img
                  key={i}
                  src={src}
                  alt={`Conseiller ${i + 1}`}
                  className={`h-10 w-10 rounded-full object-cover ring-2 ${
                    isScrolled ? "ring-background" : "ring-white/80"
                  }`}
                />
              ))}
            </div>
            {/* Green online dot - pulsing without opacity */}
            <span className="absolute -bottom-0.5 -right-0.5 flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75" />
              <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500" />
            </span>
          </div>

          {!expanded ? (
            <Button
              onClick={() => {
                setExpanded(true);
              }}
              className="bg-cta-green text-cta-green-foreground hover:bg-cta-green/90 font-semibold px-4 py-2 rounded-xl transition-all duration-300 ease-out transform hover:scale-105 hover:shadow-xl hover:shadow-cta-green/30"
            >
              <Calendar className="h-3.5 w-3.5 mr-1.5" />
              Ces conseillers sont en ligne
            </Button>
          ) : (
            <div className="animate-fade-in flex flex-col items-end relative">
              <div className={`relative flex items-center rounded-full p-0.5 ${
                isScrolled ? "bg-secondary" : "bg-white/15"
              }`}>
                <div
                  className={`absolute top-0.5 bottom-0.5 w-[calc(50%-2px)] rounded-full bg-cta-green transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] ${
                    activeMode === "rdv" ? "left-0.5" : "left-[calc(50%+2px)]"
                  }`}
                />

                <button
                  onClick={() => handleToggle("rdv")}
                  className={`relative z-10 flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium transition-colors duration-200 ${
                    activeMode === "rdv"
                      ? "text-cta-green-foreground"
                      : isScrolled
                        ? "text-foreground/60 hover:text-foreground/80"
                        : "text-white/70 hover:text-white/90"
                  }`}
                >
                  <Calendar className="h-3.5 w-3.5" />
                  Prendre rendez-vous
                </button>
                <button
                  onClick={() => handleToggle("appel")}
                  className={`relative z-10 flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium transition-colors duration-200 ${
                    activeMode === "appel"
                      ? "text-cta-green-foreground"
                      : isScrolled
                        ? "text-foreground/60 hover:text-foreground/80"
                        : "text-white/70 hover:text-white/90"
                  }`}
                >
                  <Phone className="h-3.5 w-3.5" />
                  Recevoir un appel
                </button>
              </div>

              {/* Inline phone input */}
              {activeMode === "appel" && (
                <div className={`absolute top-full right-0 mt-2 animate-fade-in rounded-xl p-3 border ${
                  isScrolled
                    ? "bg-background border-border shadow-lg"
                    : "bg-white/10 backdrop-blur-md border-white/20"
                }`}>
                  {phoneSent ? (
                    <div className="flex items-center gap-2 px-2 py-1">
                      <div className="h-7 w-7 rounded-full bg-cta-green/20 flex items-center justify-center">
                        <Check className="h-4 w-4 text-cta-green" />
                      </div>
                      <p className={`text-sm font-medium ${isScrolled ? "text-foreground" : "text-white"}`}>
                        Demande envoyée !
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="flex gap-2">
                        <Input
                          type="tel"
                          placeholder="+33 6 12 34 56 78"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          className="w-48 h-9 text-sm"
                        />
                        <Button
                          size="sm"
                          disabled={phone.length < 6 || sending}
                          onClick={handlePhoneSubmit}
                          className="bg-cta-green text-cta-green-foreground hover:bg-cta-green/90 rounded-lg h-9"
                        >
                          <Send className="h-4 w-4" />
                        </Button>
                      </div>
                      <p className={`text-[11px] mt-1.5 ${isScrolled ? "text-muted-foreground" : "text-white/60"}`}>
                        Nous vous rappelons dans les heures qui suivent
                      </p>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Meeting Dialog */}
      <Dialog open={meetingOpen} onOpenChange={setMeetingOpen}>
        <DialogContent className="max-w-4xl h-[80vh] p-0 overflow-hidden">
          <button
            onClick={() => setMeetingOpen(false)}
            className="absolute right-4 top-4 z-10 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Fermer</span>
          </button>
          <iframe
            src="https://meetings.hubspot.com/jb-dufoort"
            className="w-full h-full border-0"
            title="Prendre rendez-vous"
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SuggestionsHeaderActions;
